/*
  Shared package chip + picker + config hook.
  Reads /api/packages/config once and shares it via a module-level cache
  (simple, no context provider) so all consumers render identically.
*/
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

const toAbsUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u.startsWith("/") ? u : "/" + u}`;
};

const DEFAULTS = {
  FREE:      { label: "مجاني",   color: "#94a3b8", accent: "#475569", badge_enabled: false, badge_name: "",         badge_image_url: "" },
  VIP:       { label: "VIP",     color: "#f6c96b", accent: "#8a5a1a", badge_enabled: true,  badge_name: "VIP",      badge_image_url: "" },
  LEGENDARY: { label: "أسطوري",  color: "#a78bfa", accent: "#4c1d95", badge_enabled: true,  badge_name: "أسطوري",   badge_image_url: "" },
  ROYAL:     { label: "ملكي",    color: "#f472b6", accent: "#831843", badge_enabled: true,  badge_name: "ملكي",     badge_image_url: "" },
  STREAMER:  { label: "Streamer",color: "#c084fc", accent: "#5b21b6", badge_enabled: true,  badge_name: "Streamer", badge_image_url: "" },
};

let _cache = null;
let _subscribers = new Set();

async function fetchOnce() {
  if (_cache) return _cache;
  try {
    const { data } = await api.get("/packages/config");
    _cache = data.packages || {};
  } catch { _cache = { ...DEFAULTS }; }
  _subscribers.forEach(s => s(_cache));
  return _cache;
}

export function usePackageConfigs() {
  const [pkgs, setPkgs] = useState(_cache || DEFAULTS);
  useEffect(() => {
    let alive = true;
    fetchOnce().then(v => { if (alive) setPkgs(v); });
    const onUpd = (v) => { if (alive) setPkgs(v); };
    _subscribers.add(onUpd);
    return () => { alive = false; _subscribers.delete(onUpd); };
  }, []);
  return pkgs;
}

export function PackageChip({ plan, size = "md" }) {
  const pkgs = usePackageConfigs();
  const key = String(plan || "FREE").toUpperCase();
  const p = pkgs[key] || DEFAULTS[key] || DEFAULTS.FREE;
  const px = size === "sm" ? 10 : 12;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full font-cairo font-black"
          data-testid={`pkg-chip-${key}`}
          style={{
            background: `${p.color}22`,
            color: p.color,
            border: `1px solid ${p.color}66`,
            fontSize: `${px}px`,
            padding: size === "sm" ? "1px 8px" : "3px 10px",
          }}>
      {p.badge_enabled && p.badge_image_url && (
        <img src={toAbsUrl(p.badge_image_url)} alt=""
             className="rounded-full object-cover"
             style={{ width: size === "sm" ? 12 : 16, height: size === "sm" ? 12 : 16 }}/>
      )}
      {p.label || key}
    </span>
  );
}

/* Selectable card grid — 5 packages. Replaces the giant dropdown. */
export function PackagePicker({ value, onChange, disabled }) {
  const pkgs = usePackageConfigs();
  const KEYS = ["FREE", "VIP", "LEGENDARY", "ROYAL", "STREAMER"];
  const current = String(value || "FREE").toUpperCase();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="package-picker">
      {KEYS.map(k => {
        const p = pkgs[k] || DEFAULTS[k];
        const selected = current === k;
        return (
          <button key={k} type="button" disabled={disabled}
                  onClick={() => onChange && onChange(k)}
                  data-testid={`pkg-pick-${k}`}
                  className={`text-right rounded-lg p-2.5 transition border-2 flex flex-col items-start gap-1.5 ${
                    disabled ? "opacity-50 cursor-not-allowed" :
                    selected ? "shadow-[0_0_18px_rgba(197,160,89,0.25)]" : "hover:brightness-125"
                  }`}
                  style={{
                    background: selected ? `${p.color}18` : "#0b0b0e",
                    borderColor: selected ? p.color : "#2a2a2e",
                  }}>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full border-2 flex items-center justify-center overflow-hidden"
                   style={{ borderColor: p.color, background: p.accent }}>
                {p.badge_enabled && p.badge_image_url ? (
                  <img src={toAbsUrl(p.badge_image_url)} alt="" className="w-full h-full object-cover"/>
                ) : (
                  <span className="text-[9px] font-cairo font-black" style={{ color: p.color }}>{k.slice(0,2)}</span>
                )}
              </div>
              <span className="text-sm font-cairo font-black" style={{ color: p.color }}>{p.label || k}</span>
            </div>
            {selected && (
              <span className="text-[9px] font-cairo text-yellow-300">✓ الباقة المُختارة</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
